import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    TemplateRef,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {
    Align,
    Margin,
    PopupAnimation,
    PopupRef,
    PopupService
} from '@progress/kendo-angular-popup';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { BaseAbstractComponent } from '../../base/base-abstract.component';
import { GlobalDomEventService } from '../../../singleton-services/services/global-dom-event.service';
import { DropDownPopupItem } from '../../models/dropdown-popup-item.model';

@Component({
    standalone: false,
    selector: 'q-dropdown-popup',
    templateUrl: 'dropdown-popup.component.html',
    styleUrls: ['dropdown-popup.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class DropDownPopupComponent
    extends BaseAbstractComponent
    implements OnInit
{
    popupPositionFlip: boolean = false;
    private popupRef: PopupRef;
    private anchorAlign: Align = {
        horizontal: 'right',
        vertical: 'bottom'
    };
    private popupAlign: Align = {
        horizontal: 'right',
        vertical: 'top'
    };
    private animate: PopupAnimation = {
        direction: 'down',
        type: 'fade',
        duration: 100
    };

    @Input() list: DropDownPopupItem<any>[];
    @Input() align: 'left' | 'right';
    @Input() itemTemplate: TemplateRef<any>;
    @Input() popupTemplate: TemplateRef<any>;
    @Input() showPopupTriangle: boolean = true;
    @Input() margin: Margin;

    @Output() itemClick: EventEmitter<DropDownPopupItem<any>> =
        new EventEmitter<DropDownPopupItem<any>>();

    @ViewChild('anchor', { static: true }) public anchor: ElementRef;

    constructor(
        private globalDomEventService: GlobalDomEventService,
        private popupService: PopupService
    ) {
        super();
    }

    ngOnInit() {
        if (this.align) {
            this.anchorAlign.horizontal = this.align;
            this.popupAlign.horizontal = this.align;
        }

        // Close the pop-up if user clicks outside the native element.
        this.globalDomEventService.documentClickSubject
            .pipe(takeUntil(this.$destroy))
            .subscribe((event) => {
                if (
                    !event ||
                    (event.target &&
                        !this.anchor.nativeElement.contains(event.target))
                ) {
                    this.closePopUp();
                }
            });
    }

    /**
     * Close and dereference popup
     */
    closePopUp() {
        if (this.popupRef) {
            this.popupRef.close();
            this.popupRef = null;
        }
    }

    /**
     * Close popup before base component onDestroy
     */
    preOnDestroy() {
        this.closePopUp();
    }

    /**
     * Method to modify pop-up pointer arrow positioning, depending on whether pop-up flip is true/false
     * i.e., if the pop-up was opened above/below the anchor element.
     * @param event Pop-up position change event indicating the popup position with respect to anchor element.
     */
    handlePopupPositionChange(event: { flip: { vertical: boolean } }) {
        /* Check if flip.vertical is true while scrolling or if popup opens above anchor element on click itself. */
        if (event && event.flip && event.flip.vertical === true) {
            this.popupPositionFlip = true;
        } else {
            this.popupPositionFlip = false;
        }
    }

    onToggle(template: TemplateRef<any>): void {
        this.popupRef ? this.closePopUp() : this.openPopUp(template);
    }

    /**
     * Opens pop-up through pop-up service. Also starts the popup position change subscription.
     * @param template Template for popup dropdown.
     */
    openPopUp(template: TemplateRef<any>) {
        //Default value if no margin provided.
        if (!this.margin && this.showPopupTriangle) {
            /* To align the popup element, to the centre of the parent anchor element. */
            this.margin = {
                horizontal: this.anchor.nativeElement.scrollWidth / 2 - 17,
                vertical: 5
            };
        }
        this.popupRef = this.popupService.open({
            content: template,
            popupClass: 'q-dropdown-popup',
            collision: { horizontal: 'flip', vertical: 'flip' },
            positionMode: 'fixed',
            margin: this.margin,
            anchorAlign: this.anchorAlign,
            popupAlign: this.popupAlign,
            anchor: this.anchor,
            animate: this.animate
        });
        // Bootstrap 5 layout/grid changes can cause Kendo PopupService to compute
        // incorrect initial popup positioning near the viewport right edge.
        // Trigger a resize after render so Kendo recalculates popup placement.
        setTimeout(() => {
            this.popupRef?.popupElement?.dispatchEvent(new Event('resize'));
            window.dispatchEvent(new Event('resize'));
        });
        this.popupRef.popupAnchorViewportLeave
            .pipe(debounceTime(100), takeUntil(this.$destroy))
            .subscribe(() => {
                this.closePopUp();
            });
        this.popupRef.popupPositionChange
            .pipe(debounceTime(100), takeUntil(this.$destroy))
            .subscribe((event) => {
                this.handlePopupPositionChange(event);
            });
        this.popupRef.popupOpen.pipe(takeUntil(this.$destroy)).subscribe(() => {
            // Kendo does not call positionChange on open, we need to manually check if
            // popup is flipped on open.
            this.popupPositionFlip =
                this.popupRef.popupElement.getBoundingClientRect()['y'] <
                this.anchor.nativeElement.getBoundingClientRect()['y'];
        });
    }

    onClick(item): void {
        if (!item?.disabled) {
            this.itemClick.emit(item);
        }
    }
}
